import React, { useState } from "react";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../state/auth";

export default function AccountPage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (next.length < 8) {
      setErr("Your new password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("The new passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated", {
        description: "Use your new password the next time you sign in.",
      });
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="account-page" className="min-h-screen bg-white">
      <Header />
      <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-14 pb-24">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#c80f3f]">
          My account
        </div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#1f2a44] tracking-tight">
          Account &amp; password
        </h1>

        <Card className="mt-8 border border-[#e5e7eb] rounded-sm shadow-none">
          <div className="px-5 py-3 border-b border-[#e5e7eb] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
            Your details
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Detail label="Name" value={user?.name} testId="account-name" />
            <Detail label="Email" value={user?.email} testId="account-email" />
            <Detail label="Company" value={user?.company} testId="account-company" />
            <Detail
              label="Access level"
              value={user?.role === "admin" ? "Administrator" : "Standard user"}
              testId="account-role"
            />
          </div>
          <div className="px-5 py-3 border-t border-[#e5e7eb] text-xs text-[#4b5563] flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
            Name, company and access level are managed by your Helvar administrator.
          </div>
        </Card>

        <Card className="mt-8 border border-[#e5e7eb] rounded-sm shadow-none">
          <div className="px-5 py-3 border-b border-[#e5e7eb] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
            Change password
          </div>
          <form onSubmit={submit} className="p-5 space-y-5 max-w-md">
            <Field label="Current password">
              <Input
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                data-testid="account-current-password"
                className="rounded-sm border-[#e5e7eb] h-11"
              />
            </Field>
            <Field label="New password">
              <Input
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={8}
                data-testid="account-new-password"
                className="rounded-sm border-[#e5e7eb] h-11"
              />
              <p className="text-xs text-[#4b5563]">At least 8 characters.</p>
            </Field>
            <Field label="Confirm new password">
              <Input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="account-confirm-password"
                className="rounded-sm border-[#e5e7eb] h-11"
              />
            </Field>

            {err && (
              <div
                data-testid="account-password-error"
                className="border-l-2 border-[#c80f3f] bg-[#f4f5f7] px-4 py-3 text-sm text-[#1f2937]"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="account-change-password-submit"
              className="w-full h-11 bg-[#c80f3f] hover:bg-[#a60c34] text-white rounded-sm text-xs uppercase tracking-[0.2em] font-semibold"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <KeyRound className="h-4 w-4 mr-2" strokeWidth={1.75} />
                  Update password
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

function Detail({ label, value, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#4b5563]">
        {label}
      </div>
      <div data-testid={testId} className="mt-1 text-[#1f2a44] font-medium">
        {value || "—"}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4b5563]">
        {label}
      </Label>
      {children}
    </div>
  );
}
